import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Snackbar, Alert, Paper, Grid, Box } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const AddParty = () => {
  const [party, setParty] = useState({
      firmName: '',
      firmAddress: '',
      firmCityState: '',
      contactPerson: '',
      phoneNumber: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
      }
  }, [navigate]);

  const handleChange = (e) => {
      setParty({ ...party, [e.target.name]: e.target.value });
      setErrors({ ...errors, [e.target.name]: '' });
  };

  const validateForm = () => {
    let tempErrors = {};

    // Phone number validation (only if provided)
    if (party.phoneNumber && !/^\d{10}$/.test(party.phoneNumber)) {
        tempErrors.phoneNumber = 'Phone number must be 10 digits';
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(validateForm()){
      try {
          const response = await api.post('/parties', party);
          console.log('Party added successfully:', response.data);
          setSnackbar({ open: true, message: 'Party added successfully', severity: 'success' });
          setParty({ firmName: '', firmAddress: '', firmCityState: '', contactPerson: '', phoneNumber: '' });
      } catch (error) {
          console.error('Error adding party:', error.response?.data || error.message);
          setSnackbar({ open: true, message: `Failed to add Party: ${error.response?.data?.message || error.message}`, severity: 'error' });
      }
    }
    else {
      setSnackbar({ open: true, message: "Phone number must be 10 digits", severity: 'error' });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: 0.5,
              textAlign: 'center'
            }}
          >
            Add New Party
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Register a new customer or supplier
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="firmName"
                label="Firm Name"
                value={party.firmName}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="firmAddress"
                label="Firm Address (Optional)"
                value={party.firmAddress}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                variant="outlined"
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="firmCityState"
                label="Firm City and State"
                value={party.firmCityState}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="contactPerson"
                label="Contact Person (Optional)"
                value={party.contactPerson}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="phoneNumber"
                label="Phone Number (Optional)"
                value={party.phoneNumber}
                onChange={handleChange}
                onFocus={(e) => e.target.select()}
                variant="outlined"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowLeft size={20} />}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                flex: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Add Party
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddParty;